// Enhanced error handling utility for Supabase operations
export class SupabaseError extends Error {
  constructor(message, originalError = null, context = {}) {
    super(message);
    this.name = 'SupabaseError';
    this.originalError = originalError;
    this.context = context;
    this.timestamp = new Date().toISOString();
    this.isNetworkError = this.checkIfNetworkError(originalError);
    this.isAuthError = this.checkIfAuthError(originalError);
  }

  checkIfNetworkError(error) {
    if (!error) return false;
    const networkErrorPatterns = [
      'Failed to fetch',
      'NetworkError',
      'AuthRetryableFetchError',
      'fetch is not defined',
      'Cannot connect to database',
      'ECONNREFUSED',
      'ENOTFOUND',
      'timeout'
    ];
    return networkErrorPatterns.some(pattern => 
      error.message?.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  checkIfAuthError(error) {
    if (!error) return false;
    const authErrorPatterns = [
      'Invalid login credentials',
      'Email not confirmed',
      'User not found',
      'Signup is disabled',
      'Invalid email',
      'Too many requests',
      'unauthorized',
      'Authentication required'
    ];
    return authErrorPatterns.some(pattern => 
      error.message?.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  getUserFriendlyMessage() {
    if (this.isNetworkError) {
      return 'Connection issue. Please check your internet connection and try again.';
    }
    
    if (this.isAuthError) {
      return 'Authentication error. Please check your credentials and try again.';
    }

    // Return the original message if it's user-friendly, otherwise a generic message
    if (this.message && this.message.length < 200 && !this.message.includes('undefined')) {
      return this.message;
    }

    return 'An unexpected error occurred. Please try again.';
  }
}

// Enhanced wrapper for Supabase operations with comprehensive error handling
export const withErrorHandling = async (operation, context = {}) => {
  try {
    const result = await operation();
    
    // Handle Supabase response pattern
    if (result && typeof result === 'object' && 'data' in result && 'error' in result) {
      if (result.error) {
        throw new SupabaseError(
          result.error.message || 'Database operation failed',
          result.error,
          context
        );
      }
      return { success: true, data: result.data };
    }
    
    // Handle direct return values
    return { success: true, data: result };
    
  } catch (error) {
    console.error('Supabase operation failed:', {
      context,
      error: error.message,
      type: error.name,
      timestamp: new Date().toISOString()
    });

    // If it's already a SupabaseError, just re-throw
    if (error instanceof SupabaseError) {
      return {
        success: false,
        error: error.getUserFriendlyMessage(),
        type: error.isNetworkError ? 'network_error' : error.isAuthError ? 'auth_error' : 'unknown_error',
        originalError: error
      };
    }

    // Create a new SupabaseError for other types of errors
    const supabaseError = new SupabaseError(
      error.message || 'Unknown error occurred',
      error,
      context
    );

    return {
      success: false,
      error: supabaseError.getUserFriendlyMessage(),
      type: supabaseError.isNetworkError ? 'network_error' : supabaseError.isAuthError ? 'auth_error' : 'unknown_error',
      originalError: supabaseError
    };
  }
};

// Specific wrapper for auth operations
export const withAuthErrorHandling = async (operation, context = {}) => {
  const result = await withErrorHandling(operation, { ...context, type: 'auth' });
  
  // Add auth-specific error handling
  if (!result.success && result.originalError?.isAuthError) {
    // Log auth errors for debugging (without sensitive data)
    console.warn('Authentication error:', {
      type: result.type,
      context: context,
      timestamp: new Date().toISOString()
    });
  }
  
  return result;
};

// Wrapper for database operations
export const withDatabaseErrorHandling = async (operation, context = {}) => {
  const result = await withErrorHandling(operation, { ...context, type: 'database' });
  
  // Add database-specific error handling
  if (!result.success) {
    if (result.originalError?.isNetworkError) {
      result.error = 'Cannot connect to database. Please check your internet connection or try again later.';
    }
  }
  
  return result;
};

// Utility to check if the app is in a good state to make API calls
export const checkAPIReadiness = () => {
  const issues = [];
  
  // Check environment variables
  if (!import.meta.env.VITE_SUPABASE_URL) {
    issues.push('Missing VITE_SUPABASE_URL environment variable');
  }
  
  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    issues.push('Missing VITE_SUPABASE_ANON_KEY environment variable');
  }
  
  // Check browser features
  if (typeof fetch === 'undefined') {
    issues.push('fetch API not available');
  }
  
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    issues.push('Browser appears to be offline');
  }
  
  return {
    ready: issues.length === 0,
    issues
  };
};

// Auto-check API readiness in development
if (import.meta.env.DEV) {
  setTimeout(() => {
    const readiness = checkAPIReadiness();
    if (!readiness.ready) {
      console.warn('⚠️ API readiness issues detected:', readiness.issues);
    } else {
      console.log('✅ API readiness check passed');
    }
  }, 1000);
}